import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Signatur-spezifische Platzhalter
const SIGNATURE_PLACEHOLDERS = [
  { key: "absender_name", label: "Absender Name", description: "Name des eingeloggten Benutzers" },
  { key: "absender_email", label: "Absender E-Mail", description: "E-Mail des Absenders" },
  { key: "datum", label: "Datum", description: "Aktuelles Datum" },
];

export function MailSignatureEditor({ authToken }) {
  const { toast } = useToast();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mailEditorColors, setMailEditorColors] = useState([
    "#000000",
    "#1f2937",
    "#0f766e",
    "#2563eb",
    "#b91c1c",
  ]);
  const editorRef = useRef(null);

  function authHeaders() {
    return authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {};
  }

  async function loadEditorSettings() {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-editor-settings`, authHeaders());
      if (Array.isArray(res.data?.colors)) {
        setMailEditorColors(res.data.colors.slice(0, 5));
      }
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    async function loadSignature() {
      if (!authToken) return;
      setLoading(true);
      try {
        await loadEditorSettings();
        const res = await axios.get(`${API_BASE}/api/mail-signature`, authHeaders());
        const initialHtml = res.data?.html || "";
        setHtml(initialHtml);
      } catch (e) {
        console.error(e);
        // Wenn noch nichts existiert, einfach leer lassen
      } finally {
        setLoading(false);
      }
    }

    loadSignature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  async function handleSave() {
    if (!authToken) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/api/mail-signature`,
        { html },
        authHeaders(),
      );
      toast({ title: "Signatur gespeichert" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Signatur konnte nicht gespeichert werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="mail-signature-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">E-Mail-Signatur</h3>
          <p className="text-xs text-slate-500">
            Diese Signatur wird automatisch an alle ausgehenden E-Mails angehängt.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className="rounded-full bg-violet-600 px-4 py-2 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-50"
          data-testid="mail-signature-save-button"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Speichern...
            </span>
          ) : (
            "Signatur speichern"
          )}
        </button>
      </div>

      {/* Editor */}
      <div className={loading ? "opacity-60 pointer-events-none" : ""}>
        {loading ? (
          <div className="flex h-32 items-center justify-center rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-violet-600" />
              Signatur wird geladen...
            </div>
          </div>
        ) : (
          <ModernRichTextEditor
            ref={editorRef}
            value={html}
            onChange={setHtml}
            placeholder="Signaturtext eingeben... (z.B. Mit freundlichen Grüßen, Ihr Schwimmverein)"
            colors={mailEditorColors}
            placeholders={SIGNATURE_PLACEHOLDERS}
            showPlaceholders={true}
            minHeight="120px"
          />
        )}
      </div>

      {/* Vorschau */}
      {html && html !== "<p></p>" && (
        <div className="rounded-xl bg-slate-50 p-4">
          <div className="mb-2 text-xs font-medium text-slate-500">Vorschau:</div>
          <div 
            className="rounded-lg bg-white p-3 text-sm text-slate-700 shadow-inner prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      )}
    </div>
  );
}
